/**
 * Rip / Break form — pick a sealed lot, log pull rows, save.
 *
 * On save: each pull row becomes a brand-new Card lot tagged with Source ID =
 * the rip's ID, and the sealed lot's Qty + Total Basis + Current FMV are
 * decremented proportionally. The rip itself records Basis Removed, Pull FMV,
 * and EV Gain/Loss for evaluating long-term box-EV.
 */
import { DB } from "../lib/db";
import { escapeHtml, toast, $, $$ } from "../lib/dom";
import { fmt$, fmtDate, todayISO } from "../utils/format";
import { openModal, closeModal, markModalDirty } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { wireCombobox } from "../components/combobox";
import { wireLotSearch } from "../components/lotSearch";
import { SetCache } from "../lib/sets";
import { quickCardLookup } from "../components/quickCardLookup";
import { nextId } from "../utils/ids";
import { render } from "../state";
import { BULK_CLASSES, bulkUnitValueFor, addToBulkPool, type BulkClass } from "../lib/bulk";
import { pullBasisFMVForRip, lockedPullFMVForRip } from "../lib/derived";
import { reallocateRipBasis } from "../lib/ripBasis";
import { type InvEffect } from "../lib/inventoryEffects";
import { normalizeSetName } from "../lib/setNames";

export async function startRip(prefSealedId?: string): Promise<void> {
  // Pull lot-bearing tables fresh so the sealed picker reflects cloud-current qty/FMV.
  await Promise.all([DB.refreshTable("sealed"), DB.refreshTable("cards")]);
  const initialLot = prefSealedId
    ? DB.table("sealed").find(s => s["Lot ID"] === prefSealedId)
    : null;
  const ripId = nextId("R", "rips", "Rip ID");

  const body = `
    <form id="ripForm" class="space-y-3" onsubmit="return false;">
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        ${formField({ label: "Rip ID", name: "Rip ID", value: ripId, required: true })}
        ${formField({ label: "Date", name: "Date", type: "date", value: todayISO(), required: true })}
        ${formField({ label: "Qty Opened", name: "Qty Opened", type: "number", step: "1", value: 1, required: true })}
      </div>
      <div>
        <label class="label">Sealed Lot</label>
        <div class="combobox-wrapper" data-rip-sealed-search>
          <input class="input" autocomplete="off" placeholder="Type product, set, or lot ID — e.g. 'surging' or 'S-26-0001'">
          <div class="combobox-list"></div>
        </div>
        <input type="hidden" id="ripSealedLot" name="Sealed Lot ID" value="">
        <div id="ripSealedSummary" class="text-xs mt-1 text-[var(--muted)]">No lot picked yet.</div>
      </div>
      ${formField({ label: "Notes", name: "Notes", type: "textarea", placeholder: "Pull highlights, channel/stream notes, etc." })}

      <div class="panel-2 p-3 mt-2">
        <div class="flex items-center justify-between mb-2">
          <h4 class="font-semibold">Pulls (cards added to inventory)</h4>
          <button type="button" class="btn btn-primary" onclick="addPullRow()">+ Add Pull</button>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">Each row creates a new card lot when you save the rip. Click ⌕ on a row to look up the card on the Pokémon TCG API (auto-fills name, set, number, and FMV).</div>
        <div class="scrollx"><table class="t" id="pullTable">
          <thead><tr><th>Card Name</th><th>Set</th><th>#</th><th>Condition</th><th class="text-right">Qty</th><th class="text-right">FMV</th><th></th><th></th></tr></thead>
          <tbody id="pullBody"></tbody>
        </table></div>
      </div>

      <div class="panel-2 p-3 mt-2">
        <div class="flex items-center justify-between mb-2">
          <h4 class="font-semibold">Bulk additions</h4>
          <button type="button" class="btn btn-primary" onclick="addBulkPullRow()">+ Add Bulk</button>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">Bumps the matching pool (Bulk / Holo Bulk / Premium Bulk) on save. Per-card value comes from Configs → Defaults.</div>
        <div class="scrollx"><table class="t" id="bulkPullTable">
          <thead><tr><th>Class</th><th class="text-right">Qty</th><th class="text-right">Unit Value</th><th class="text-right">Line Total</th><th></th></tr></thead>
          <tbody id="bulkPullBody"></tbody>
        </table></div>
      </div>
    </form>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveRip()">Save Rip</button>`;
  openModal({ title: "New Box Rip / Break", body, footer, wide: true });

  // Sealed-lot autocomplete
  const wrapper = document.querySelector<HTMLElement>('[data-rip-sealed-search]')!;
  const hidden  = document.getElementById("ripSealedLot") as HTMLInputElement;
  const summary = document.getElementById("ripSealedSummary");
  const wrapperInput = wrapper.querySelector<HTMLInputElement>("input")!;

  const setPicked = (lot: { id: string; product?: string; qty: number; basis: number }) => {
    hidden.value = lot.id;
    wrapperInput.value = `${lot.id} · ${lot.product || ""}`.trim().replace(/ ·\s*$/, "");
    if (summary) summary.innerHTML = `<span class="text-emerald-300">✓ ${escapeHtml(lot.id)}</span> · ${escapeHtml(lot.product || "")} · qty ${lot.qty} · unit basis ${fmt$(lot.basis)}`;
  };

  wireLotSearch(wrapper, {
    kind: "sealed",
    onSelect: (lot) => setPicked(lot),
  });

  // Pre-select if the user clicked Rip from a specific sealed row
  if (initialLot) {
    setPicked({
      id: initialLot["Lot ID"],
      product: initialLot["Product"] || "",
      qty: Number(initialLot["Qty"]) || 0,
      basis: Number(initialLot["Unit Basis"]) || 0,
    });
  }
  addPullRow();
}

export function addBulkPullRow(): void {
  const tbody = $("#bulkPullBody");
  if (!tbody) return;
  const tr = document.createElement("tr");
  tr.setAttribute("data-bulk-row", "1");
  const defaultUnit = bulkUnitValueFor("Bulk");
  tr.innerHTML = `
    <td>
      <select class="select !py-1 !px-2 text-sm" name="bulk_class" onchange="updateBulkPullRow(this)">
        ${BULK_CLASSES.map(k => `<option value="${escapeHtml(k)}">${escapeHtml(k)}</option>`).join("")}
      </select>
    </td>
    <td><input class="input !py-1 !px-2 text-sm w-20 text-right" name="bulk_qty" type="number" step="1" min="1" value="1" oninput="updateBulkPullRow(this)"></td>
    <td class="text-right stat-num text-[var(--muted)]" data-bulk-unit>${fmt$(defaultUnit)}</td>
    <td class="text-right stat-num" data-bulk-total>${fmt$(defaultUnit)}</td>
    <td><button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="this.closest('tr').remove()">✕</button></td>
  `;
  tbody.appendChild(tr);
}

/** Re-read the row's class + qty and refresh the read-only unit and total cells. */
export function updateBulkPullRow(originEl: HTMLElement): void {
  const tr = originEl.closest("tr");
  if (!tr) return;
  const classEl = tr.querySelector<HTMLSelectElement>('select[name="bulk_class"]');
  const qtyEl   = tr.querySelector<HTMLInputElement>('input[name="bulk_qty"]');
  const unitCell  = tr.querySelector<HTMLElement>('[data-bulk-unit]');
  const totalCell = tr.querySelector<HTMLElement>('[data-bulk-total]');
  const klass = (classEl?.value || "Bulk") as BulkClass;
  const qty = Math.max(0, Number(qtyEl?.value) || 0);
  const unit = bulkUnitValueFor(klass);
  if (unitCell) unitCell.textContent = fmt$(unit);
  if (totalCell) totalCell.textContent = fmt$(qty * unit);
}

export function addPullRow(): void {
  const tbody = $("#pullBody");
  if (!tbody) return;
  const tr = document.createElement("tr");
  tr.innerHTML = `
    <td><input class="input !py-1 !px-2 text-sm" name="pull_name" placeholder="e.g. Charizard ex or 'pikachu TG05'"></td>
    <td>
      <div class="combobox-wrapper" data-pull-set>
        <input class="input !py-1 !px-2 text-sm" name="pull_set" autocomplete="off" placeholder="Type to search…">
        <div class="combobox-list"></div>
      </div>
    </td>
    <td><input class="input !py-1 !px-2 text-sm w-16" name="pull_num"></td>
    <td><select class="select !py-1 !px-2 text-sm" name="pull_cond">
      ${["Near Mint", "Mint", "Lightly Played", "Moderately Played", "Heavily Played", "Other"].map(o => `<option>${o}</option>`).join("")}
    </select></td>
    <td><input class="input !py-1 !px-2 text-sm w-16 text-right" name="pull_qty" type="number" step="1" value="1"></td>
    <td><input class="input !py-1 !px-2 text-sm w-24 text-right" name="pull_fmv" type="number" step="0.01"></td>
    <td><button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="lookupForRipPull(this)" title="Find this card on the Pokémon TCG API (auto-fills name, set, #, FMV)">⌕ Lookup</button></td>
    <td><button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="this.closest('tr').remove()">✕</button></td>
  `;
  tbody.appendChild(tr);

  // Wire Set combobox to follow the Game of the currently-selected sealed lot
  const wrapper = tr.querySelector<HTMLElement>('[data-pull-set]');
  if (wrapper) {
    const getRipGame = () => {
      const sealedSel = document.getElementById("ripSealedLot") as HTMLInputElement | null;
      const lotId = sealedSel?.value || "";
      const lot = lotId ? DB.table("sealed").find(s => s["Lot ID"] === lotId) : null;
      return (lot && lot.Game) || "Pokemon";
    };
    wireCombobox(wrapper, {
      getOptions: () => SetCache.get(getRipGame()),
      asyncLoader: () => SetCache.ensureFresh(getRipGame()),
    });
  }
}

/** Open the Pokémon TCG card picker for a single pull row. On pick, fills
 *  the row's Card Name / Set / Card # / FMV / Condition (if grade-style) and
 *  marks the modal dirty so navigation away prompts. */
export function lookupForRipPull(btnEl: HTMLElement): void {
  const tr = btnEl.closest("tr");
  if (!tr) return;
  const nameInp = tr.querySelector<HTMLInputElement>('input[name="pull_name"]');
  const setWrapInp = tr.querySelector<HTMLInputElement>('[data-pull-set] input');
  const numInp  = tr.querySelector<HTMLInputElement>('input[name="pull_num"]');
  const fmvInp  = tr.querySelector<HTMLInputElement>('input[name="pull_fmv"]');

  (window as any)._lookupOnPick = (c: any) => {
    if (nameInp)    nameInp.value = c.name || "";
    if (setWrapInp) setWrapInp.value = (c.set && c.set.name) || setWrapInp.value;
    if (numInp)     numInp.value  = c.number || "";
    const tcg = c.tcgplayer && c.tcgplayer.prices;
    if (tcg && fmvInp) {
      const p = Object.values<any>(tcg).find(x => x && typeof x.market === "number");
      if (p) fmvInp.value = String(+Number(p.market).toFixed(2));
    }
    markModalDirty();
    toast(`Picked: ${c.name}${c.set?.name ? " · " + c.set.name : ""}`);
    setTimeout(() => { delete (window as any)._lookupOnPick; }, 100);
  };
  const seed = (nameInp?.value || "").trim();
  if (!seed) { toast("Type a card name (or 'name 123') first", "err"); return; }
  const sealedSel = document.getElementById("ripSealedLot") as HTMLInputElement | null;
  const ripLot = sealedSel?.value ? DB.table("sealed").find(sl => sl["Lot ID"] === sealedSel.value) : null;
  const narrow: { number?: string; set?: string; game?: string } = { game: (ripLot && ripLot["Game"]) || "Pokemon" };
  if (numInp?.value)       narrow.number = numInp.value.trim();
  if (setWrapInp?.value)   narrow.set    = setWrapInp.value.trim();
  quickCardLookup(1, seed, narrow);
}

export async function saveRip(): Promise<void> {
  const form = $("#ripForm");
  if (!form) return;
  const data = readForm(form);
  if (!data["Sealed Lot ID"]) { toast("Pick a sealed lot", "err"); return; }
  const sealedLot = DB.table("sealed").find(s => s["Lot ID"] === data["Sealed Lot ID"]);
  if (!sealedLot) { toast("Sealed lot not found", "err"); return; }
  const qtyOpened = Number(data["Qty Opened"]) || 0;
  if ((Number(sealedLot["Qty"]) || 0) < qtyOpened) {
    if (!confirm(`Lot only has ${sealedLot["Qty"]} remaining. Open ${qtyOpened} anyway?`)) return;
  }
  const unitBasis = Number(sealedLot["Unit Basis"]) || 0;
  const basisRemoved = +(unitBasis * qtyOpened).toFixed(2);

  // Collect singles pulls from each <tr> in #pullBody
  const pulls: Array<Record<string, any>> = [];
  $$("#pullBody tr").forEach(tr => {
    const f = readForm(tr);
    if (!f["pull_name"]) return;
    pulls.push(f);
  });
  const singlesFMV = pulls.reduce((acc, p) => acc + (Number(p.pull_qty) || 1) * (Number(p.pull_fmv) || 0), 0);

  // Collect bulk rows from each <tr> in #bulkPullBody
  const bulkRows: Array<{ klass: BulkClass; qty: number; unit: number; lineTotal: number }> = [];
  $$("#bulkPullBody tr").forEach(tr => {
    const f = readForm(tr);
    const klass = (f["bulk_class"] || "Bulk") as BulkClass;
    const qty = Number(f["bulk_qty"]) || 0;
    if (!qty || !BULK_CLASSES.includes(klass)) return;
    const unit = bulkUnitValueFor(klass);
    bulkRows.push({ klass, qty, unit, lineTotal: +(qty * unit).toFixed(2) });
  });
  const bulkFMV = bulkRows.reduce((acc, b) => acc + b.lineTotal, 0);

  const pullFMV = +(singlesFMV + bulkFMV).toFixed(2);

  // Create a card lot for each singles pull. Each pull's FMV at rip time is
  // frozen as its permanent basis-allocation weight (see ripBasis.ts).
  const allocWeights: Record<string, number> = {};
  const newIds: string[] = [];
  const ripEffect: InvEffect = [];
  const cardAdds: Promise<void>[] = [];
  pulls.forEach(p => {
    const cid = nextId("C", "cards", "Card Lot ID");
    const qty = Number(p.pull_qty) || 1;
    const fmv = Number(p.pull_fmv) || 0;
    cardAdds.push(DB.add("cards", {
      "Card Lot ID": cid,
      "Game":       sealedLot["Game"] || "Pokemon",
      "Card Name":  p.pull_name,
      "Set":        normalizeSetName(p.pull_set || sealedLot["Set"] || ""),
      "Card #":     p.pull_num || "",
      "Inventory Class": "Raw",
      "Condition/Grade": p.pull_cond || "Near Mint",
      "Qty": qty,
      "Acq Date": data["Date"],
      "Acq Type": "Pull/Rip",
      "Valuation Source": "TCGPlayer",
      "Unit Basis": 0,
      "Total Basis": 0,
      "Current FMV": +(qty * fmv).toFixed(2),
      "Unrealized G/L": +(qty * fmv).toFixed(2),
      "Status": "Available",
      "Source ID": data["Rip ID"],
      "Notes": "From rip " + data["Rip ID"],
    }));
    allocWeights[cid] = +(qty * fmv).toFixed(2);
    newIds.push(cid);
    ripEffect.push({ kind: "lotCreate", table: "cards", lotId: cid });
  });

  // Apply bulk additions — each row bumps the matching pool's qty.
  for (const b of bulkRows) {
    addToBulkPool(b.klass, b.qty);
    ripEffect.push({ kind: "poolDelta", klass: b.klass, dQty: b.qty });
  }

  // Decrement the sealed lot (recording the exact delta for reversal).
  const curSealedQty = Number(sealedLot["Qty"]) || 0;
  const newQty = Math.max(0, curSealedQty - qtyOpened);
  const newTotalBasis = +(unitBasis * newQty).toFixed(2);
  const oldQty = Math.max(curSealedQty, 1);
  const oldSealedFMV = Number(sealedLot["Current FMV"]) || 0;
  const newSealedFMV = +((oldSealedFMV / oldQty) * newQty).toFixed(2);
  const ripPatch: Record<string, any> = {
    "Qty": newQty,
    "Total Basis": newTotalBasis,
    "Current FMV": newSealedFMV,
  };
  if (newQty === 0) ripPatch["Status"] = "Opened";
  DB.update("sealed", "Lot ID", sealedLot["Lot ID"], ripPatch);
  ripEffect.push({ kind: "lotDelta", table: "sealed", lotId: sealedLot["Lot ID"], dQty: newQty - curSealedQty, dFMV: newSealedFMV - oldSealedFMV });

  // Persist the rip itself. Bulk Pulls is the audit-trail record of which pools
  // each rip contributed to, so Lot History can show "+100 from R-26-0019".
  DB.add("rips", {
    "Rip ID":          data["Rip ID"],
    "Date":            data["Date"],
    "Sealed Lot ID":   sealedLot["Lot ID"],
    "Sealed Product":  sealedLot["Product"],
    "Qty Opened":      qtyOpened,
    "Unit Basis":      unitBasis,
    "Basis Removed":   basisRemoved,
    "Pull FMV":        pullFMV,
    "EV Gain/Loss":    +(pullFMV - basisRemoved).toFixed(2),
    "New Card Lot IDs": newIds.join(", "),
    "Bulk Pulls":      bulkRows.map(b => ({ "Class": b.klass, "Qty": b.qty, "Unit": b.unit, "Line Total": b.lineTotal })),
    "Alloc Weights":   allocWeights,
    "Inv Effect":      ripEffect,
    "Notes":           data["Notes"] || "",
  });

  // Wait for the pulled-card inserts to commit (registering their cloud UUIDs)
  // BEFORE reallocating basis. Otherwise the basis UPDATE can outrun the still
  // in-flight INSERT, the DB layer fails to find the row and re-inserts it, and
  // the second insert trips the cards (user_id, lot_id) unique constraint.
  try { await Promise.all(cardAdds); } catch { /* insert failures already toasted by the DB layer */ }
  reallocateRipBasis(data["Rip ID"]);

  const bulkSummary = bulkRows.length
    ? ` · ${bulkRows.reduce((s, b) => s + b.qty, 0)} bulk cards into ${bulkRows.length} pool${bulkRows.length === 1 ? "" : "s"}`
    : "";
  toast(`Rip saved · ${pulls.length} card lot(s) created${bulkSummary}`);
  closeModal();
  render();
}

/* ============================================================
   "Edit Rip" — shows the full rip context (sealed lot + pulled cards
   + bulk pulls) plus editable meta fields (date / notes / pull FMV /
   basis). Pulled card lots themselves are edited individually on the
   Cards tab — click any Lot ID below to jump to its history, where the
   row can also be opened in the Cards editor.
   ============================================================ */
export function editRipMeta(id: string): void {
  const r = DB.table("rips").find(x => x["Rip ID"] === id);
  if (!r) return;

  // Sealed lot info (best-effort — the lot may have been fully consumed/deleted)
  const sealedId = r["Sealed Lot ID"] || "";
  const sealedLot = sealedId ? DB.table("sealed").find(s => s["Lot ID"] === sealedId) : null;
  const sealedProduct = (sealedLot && sealedLot["Product"]) || r["Sealed Product"] || "(unknown product)";
  const sealedSet = (sealedLot && sealedLot["Set"]) || "";
  const qtyOpened = Number(r["Qty Opened"]) || 1;
  const unitBasis = Number(r["Unit Basis"]) || (sealedLot ? Number(sealedLot["Unit Basis"]) || 0 : 0);

  // Pulled card lots (everything in cards table whose Source ID matches this rip)
  const pulled = DB.table("cards").filter(c => c["Source ID"] === r["Rip ID"]);
  let cardsFMVTotal = 0;
  const pulledRows = pulled.length
    ? pulled.map(c => {
        const cid = escapeHtml(c["Card Lot ID"] || "");
        const qty = Number(c["Qty"]) || 0;
        const totalFMV = Number(c["Current FMV"]) || 0;
        const unitFMV = qty > 0 ? +(totalFMV / qty).toFixed(2) : totalFMV;
        cardsFMVTotal += totalFMV;
        return `<tr>
          <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${cid}')">${cid}</a></td>
          <td>${escapeHtml(c["Card Name"] || "")}</td>
          <td class="text-xs text-[var(--muted)]">${escapeHtml(c["Set"] || "")}</td>
          <td class="text-xs text-[var(--muted)]">${escapeHtml(c["Card #"] || "")}</td>
          <td class="text-xs">${escapeHtml(c["Condition/Grade"] || "")}</td>
          <td class="text-right stat-num">${qty}</td>
          <td class="text-right stat-num">${fmt$(unitFMV)}</td>
          <td class="text-right stat-num">${fmt$(totalFMV)}</td>
          <td><button class="btn btn-ghost !py-0.5 !px-2 text-xs" onclick="closeModal(); editCard('${cid}')" title="Open this card lot in the Card editor">✎ Edit</button></td>
        </tr>`;
      }).join("")
    : `<tr><td colspan="9" class="text-center text-[var(--muted)] py-2 text-xs">No card lots found with Source ID = ${escapeHtml(r["Rip ID"])}. They may have been deleted.</td></tr>`;
  cardsFMVTotal = +cardsFMVTotal.toFixed(2);

  // Bulk pulls audit trail
  const bulkPulls: Array<{Class?: string; Qty?: number; Unit?: number; "Line Total"?: number}> = Array.isArray(r["Bulk Pulls"]) ? r["Bulk Pulls"] : [];
  // Use shared helper so the modal, main Rips table, and Lot History all agree.
  // Equivalent to (cardsFMVTotal + sum of bulk Line Totals).
  const computedPullFMV = pullBasisFMVForRip(r);          // live total of current cards
  const lockedFMV = lockedPullFMVForRip(r);              // frozen snapshot stored on the rip
  const bulkBlock = bulkPulls.length
    ? `<div class="panel-2 p-3 mt-2">
         <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">Bulk Pulls (${bulkPulls.length})</div>
         <table class="t"><thead><tr><th>Class</th><th class="text-right">Qty</th><th class="text-right">Unit</th><th class="text-right">Line Total</th></tr></thead><tbody>
           ${bulkPulls.map(b => `<tr>
             <td>${escapeHtml(String(b["Class"] || ""))}</td>
             <td class="text-right stat-num">${b["Qty"] || 0}</td>
             <td class="text-right stat-num text-[var(--muted)]">${fmt$(b["Unit"])}</td>
             <td class="text-right stat-num">${fmt$(b["Line Total"])}</td>
           </tr>`).join("")}
         </tbody></table>
       </div>`
    : "";

  const body = `
    <form id="ripMetaForm" class="space-y-3" onsubmit="return false;">
      <input type="hidden" id="ripSealedLot" value="${escapeHtml(sealedId)}">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        ${formField({ label: "Rip ID", name: "Rip ID", value: r["Rip ID"] })}
        ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(r["Date"]) })}
      </div>

      <div class="panel-2 p-3">
        <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">Sealed Lot Opened</div>
        ${sealedId
          ? `<div class="text-sm">
              <a class="font-mono text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${escapeHtml(sealedId)}')">${escapeHtml(sealedId)}</a>
              · <b>${escapeHtml(sealedProduct)}</b>${sealedSet ? ` <span class="text-xs text-[var(--muted)]">(${escapeHtml(sealedSet)})</span>` : ""}
            </div>
            <div class="text-xs text-[var(--muted)] mt-1">Qty Opened: <b>${qtyOpened}</b> · Unit Basis: <b>${fmt$(unitBasis)}</b>${sealedLot ? "" : ` · <span class="text-amber-300">Source lot no longer in inventory</span>`}</div>`
          : `<div class="text-sm text-[var(--muted)]">No sealed lot recorded.</div>`
        }
      </div>

      <div class="panel-2 p-3">
        <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-2">Cards Pulled (${pulled.length})</div>
        <div class="scrollx"><table class="t">
          <thead><tr><th>Lot ID</th><th>Card</th><th>Set</th><th>#</th><th>Cond</th><th class="text-right">Qty</th><th class="text-right">Unit FMV</th><th class="text-right">Total FMV</th><th></th></tr></thead>
          <tbody>${pulledRows}</tbody>
          <tfoot><tr>
            <td colspan="7" class="text-right text-xs text-[var(--muted)] font-semibold uppercase tracking-wider">Cards FMV subtotal</td>
            <td class="text-right stat-num font-semibold">${fmt$(cardsFMVTotal)}</td>
            <td></td>
          </tr></tfoot>
        </table></div>
        <div class="text-xs text-[var(--muted)] mt-2">Missed some? Add them in the <b>Add missed pulls</b> section below. To remove a pull, open it (\u270e Edit) and delete it or clear its Source ID, then re-open this rip and Save \u2014 the box basis re-spreads across the remaining pulls.</div>
      </div>

      ${bulkBlock}

      <div class="panel-2 p-3">
        <div class="flex items-center justify-between mb-2">
          <div class="text-xs font-semibold text-[var(--muted)] uppercase tracking-wider">Add missed pulls</div>
          <div class="flex gap-2">
            <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addPullRow()">+ Add Pull</button>
            <button type="button" class="btn btn-ghost !py-1 !px-3 text-xs" onclick="document.getElementById('bulkPullTable')?.classList.remove('hidden'); addBulkPullRow()">+ Add Bulk</button>
          </div>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">For pulls you missed when logging the rip. On Save, each row becomes a new card lot attached to this rip: the box's Basis Removed re-spreads across <b>all</b> pulls (frozen FMV weights), and the locked Pull FMV / EV bump up by what you add. The sealed lot is <b>not</b> decremented again.</div>
        <div class="scrollx"><table class="t" id="pullTable">
          <thead><tr><th>Card Name</th><th>Set</th><th>#</th><th>Condition</th><th class="text-right">Qty</th><th class="text-right">FMV</th><th></th><th></th></tr></thead>
          <tbody id="pullBody"></tbody>
        </table></div>
        <div class="scrollx mt-2"><table class="t hidden" id="bulkPullTable">
          <thead><tr><th>Bulk Class</th><th class="text-right">Qty</th><th class="text-right">Unit Value</th><th class="text-right">Line Total</th><th></th></tr></thead>
          <tbody id="bulkPullBody"></tbody>
        </table></div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="label">Pull FMV (at rip) — locked</label>
          <div class="flex gap-2">
            <input class="input" type="number" name="Pull FMV" value="${lockedFMV}" step="0.01" title="Frozen snapshot of this rip's pull value. It stays put when pulled cards are sold or traded.">
            <button type="button" class="btn btn-ghost shrink-0" onclick="recaptureRipPullFMV('${escapeHtml(r["Rip ID"])}')" title="Lock in the current total FMV of the pulled cards (Σ Current FMV + bulk line totals)">↻ Recapture</button>
          </div>
          <div class="text-xs text-[var(--muted)] mt-1">This value is <b>frozen</b> — it won't drop when you sell or trade pulled cards, so a profitable box keeps showing its gain. Live total of the pulled cards right now: <b>${fmt$(computedPullFMV)}</b> (across ${pulled.length} lot${pulled.length === 1 ? "" : "s"}${bulkPulls.length ? ` + ${bulkPulls.length} bulk` : ""}). Click <b>↻ Recapture</b> to lock that in (e.g. after refining card FMVs), then Save.</div>
        </div>
        <div>
          <label class="label" for="f_Basis_Removed">Basis Removed ($)</label>
          <div class="flex gap-2">
            <input class="input flex-1" id="f_Basis_Removed" name="Basis Removed" type="number" step="0.01" value="${r["Basis Removed"] ?? ""}" readonly>
            <button type="button" class="btn btn-ghost whitespace-nowrap" id="ripBasisOverrideBtn" title="Unlock for a deliberate correction">✎ Override</button>
          </div>
          <div class="text-xs text-[var(--muted)] mt-1">Derived: sealed lot's Unit Basis (${fmt$(unitBasis)}) × Qty Opened (${qtyOpened}). This number is the box cost the allocation engine distributes across the pulls — Books Health reconciles against it. Override only for deliberate corrections.</div>
        </div>
      </div>
      ${formField({ label: "Notes", name: "Notes", type: "textarea", value: r["Notes"] })}
    </form>`;
  const safeId = escapeHtml(r["Rip ID"]);
  const footer = `
    <button class="btn btn-danger" onclick="deleteRow('rips','Rip ID','${safeId}')">Delete Rip</button>
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveRipMeta('${safeId}')">Save</button>`;
  openModal({ title: "Edit Rip", body, footer, wide: true });

  // Basis Removed soft lock — same consequence-confirm pattern as Unit Basis.
  document.getElementById("ripBasisOverrideBtn")?.addEventListener("click", () => {
    if (!confirm(`Basis Removed is derived from the sealed lot (${fmt$(unitBasis)} \u00d7 ${qtyOpened}). Changing it re-targets the ENTIRE basis allocation for this rip's pulls and moves what Books Health reconciles against.\n\nOverride it?`)) return;
    const inp = document.getElementById("f_Basis_Removed") as HTMLInputElement | null;
    if (inp) { inp.readOnly = false; inp.focus(); inp.select(); }
    document.getElementById("ripBasisOverrideBtn")?.remove();
  });
}

export function recaptureRipPullFMV(id: string): void {
  const r = DB.table("rips").find(x => x["Rip ID"] === id);
  if (!r) return;
  const live = pullBasisFMVForRip(r);
  const inp = document.querySelector<HTMLInputElement>('#ripMetaForm [name="Pull FMV"]');
  if (inp) {
    inp.value = String(live);
    markModalDirty();
    toast(`Locked Pull FMV recaptured: ${fmt$(live)} — click Save to keep it`);
  }
}

export async function saveRipMeta(id: string): Promise<void> {
  const r = DB.table("rips").find(x => x["Rip ID"] === id);
  if (!r) { toast("Rip not found", "err"); return; }
  const d = readForm($("#ripMetaForm"));
  // The pull/bulk rows live inside #ripMetaForm, so readForm picks their named
  // inputs up too — strip them so they never leak onto the rip row itself.
  for (const k of Object.keys(d)) if (k.startsWith("pull_") || k.startsWith("bulk_")) delete d[k];

  // ---- Late pulls: rows in the "Add missed pulls" section ----
  const pulls: Array<Record<string, any>> = [];
  $$("#pullBody tr").forEach(tr => {
    const f = readForm(tr);
    if (f["pull_name"]) pulls.push(f);
  });
  const bulkRows: Array<{ klass: BulkClass; qty: number; unit: number; lineTotal: number }> = [];
  $$("#bulkPullBody tr").forEach(tr => {
    const f = readForm(tr);
    const klass = (f["bulk_class"] || "Bulk") as BulkClass;
    const qty = Number(f["bulk_qty"]) || 0;
    if (!qty || !BULK_CLASSES.includes(klass)) return;
    const unit = bulkUnitValueFor(klass);
    bulkRows.push({ klass, qty, unit, lineTotal: +(qty * unit).toFixed(2) });
  });

  const cardAdds: Promise<void>[] = [];
  if (pulls.length || bulkRows.length) {
    const sealedLot = r["Sealed Lot ID"] ? DB.table("sealed").find(sl => sl["Lot ID"] === r["Sealed Lot ID"]) : null;
    const allocWeights: Record<string, number> = (r["Alloc Weights"] && typeof r["Alloc Weights"] === "object")
      ? { ...(r["Alloc Weights"] as Record<string, number>) } : {};
    const invEffect: InvEffect = Array.isArray(r["Inv Effect"]) ? [...r["Inv Effect"]] : [];
    const bulkAudit: any[] = Array.isArray(r["Bulk Pulls"]) ? [...r["Bulk Pulls"]] : [];
    const prevIds = String(r["New Card Lot IDs"] || "").split(",").map(x => x.trim()).filter(Boolean);
    const newIds: string[] = [];
    let addedFMV = 0;

    pulls.forEach(pl => {
      const cid = nextId("C", "cards", "Card Lot ID");
      const qty = Number(pl.pull_qty) || 1;
      const fmv = Number(pl.pull_fmv) || 0;
      cardAdds.push(DB.add("cards", {
        "Card Lot ID": cid,
        "Game":       (sealedLot && sealedLot["Game"]) || "Pokemon",
        "Card Name":  pl.pull_name,
        "Set":        normalizeSetName(pl.pull_set || (sealedLot && sealedLot["Set"]) || ""),
        "Card #":     pl.pull_num || "",
        "Inventory Class": "Raw",
        "Condition/Grade": pl.pull_cond || "Near Mint",
        "Qty": qty,
        "Acq Date": d["Date"] || r["Date"],
        "Acq Type": "Pull/Rip",
        "Valuation Source": "TCGPlayer",
        "Unit Basis": 0,
        "Total Basis": 0,
        "Current FMV": +(qty * fmv).toFixed(2),
        "Unrealized G/L": +(qty * fmv).toFixed(2),
        "Status": "Available",
        "Source ID": id,
        "Notes": "Late-added pull from rip " + id,
      }));
      allocWeights[cid] = +(qty * fmv).toFixed(2);
      newIds.push(cid);
      invEffect.push({ kind: "lotCreate", table: "cards", lotId: cid });
      addedFMV += qty * fmv;
    });

    for (const b of bulkRows) {
      addToBulkPool(b.klass, b.qty);
      invEffect.push({ kind: "poolDelta", klass: b.klass, dQty: b.qty });
      bulkAudit.push({ "Class": b.klass, "Qty": b.qty, "Unit": b.unit, "Line Total": b.lineTotal });
      addedFMV += b.lineTotal;
    }

    // The frozen Pull FMV snapshot grows by exactly what was added, so EV
    // reflects the whole box. Basis Removed is untouched — same box cost,
    // now spread across more pulls (siblings dilute, conservation holds).
    d["Pull FMV"]         = +((Number(d["Pull FMV"]) || 0) + addedFMV).toFixed(2);
    d["New Card Lot IDs"] = [...prevIds, ...newIds].join(", ");
    d["Alloc Weights"]    = allocWeights;
    d["Inv Effect"]       = invEffect;
    d["Bulk Pulls"]       = bulkAudit;
  }

  d["EV Gain/Loss"] = +((Number(d["Pull FMV"]) || 0) - (Number(d["Basis Removed"]) || 0)).toFixed(2);
  DB.update("rips", "Rip ID", id, d);
  // Wait for the new card inserts to register their cloud UUIDs BEFORE the
  // basis reallocation updates them (same race guard as saveRip).
  try { await Promise.all(cardAdds); } catch { /* insert failures already toasted by the DB layer */ }
  // Basis Removed and/or the pull set may have changed — redistribute so every
  // pulled lot's Unit Basis stays in sync with the rip's totals.
  reallocateRipBasis(id);
  toast(pulls.length || bulkRows.length
    ? `Rip updated \u00b7 ${pulls.length} pull(s) attached${bulkRows.length ? ` \u00b7 ${bulkRows.reduce((a, b) => a + b.qty, 0)} bulk cards` : ""} \u00b7 basis re-spread`
    : "Rip updated");
  closeModal();
  render();
}

// Inline-handler bridges
(window as any).startRip          = startRip;
(window as any).saveRip           = saveRip;
(window as any).addPullRow        = addPullRow;
(window as any).addBulkPullRow    = addBulkPullRow;
(window as any).updateBulkPullRow = updateBulkPullRow;
(window as any).lookupForRipPull  = lookupForRipPull;
(window as any).editRipMeta       = editRipMeta;
(window as any).saveRipMeta       = saveRipMeta;
(window as any).recaptureRipPullFMV = recaptureRipPullFMV;
